import React, { Dispatch, SetStateAction } from "react";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	DeliveryEstimationApi,
	LiveOrderApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { isExternalOrderDelivery } from "../../../../../../services/orderDelivery";
import {
	LiveOrderDeliveryOptionType,
	defaultDeliveryTimeOptions,
	woltDriveTimeOptions,
} from "../../../../../../services/types";
import { getDefaultDeliveryOption, getNumberOfMinutesFromNowToTheDate } from "../../../../../../services/utils";
import { useEstimateDelivery } from "../../hooks/useEstimateDelivery";
import LiveOrderDeliveryTimeOption from "../LiveOrderDeliveryTimeOptions/LiveOrderDeliveryTimeOption";

interface Props {
	selectedDeliveryOption: LiveOrderDeliveryOptionType | undefined;
	order: LiveOrderApi;
	form: UseFormReturn<{ scheduled_delivery_at?: string }>;
	setEstimatedDeliveryData: (data: DeliveryEstimationApi | undefined) => void;
	setSelectedTimeOption: (option: string | number | undefined) => void;
	selectedTimeOption: number | string | undefined;
	orderDate: string | undefined;
	estimatingLoading: boolean;
	setEstimatingLoading: Dispatch<SetStateAction<boolean>>;
}

const LiveOrderAssignDeliveryEmployeeModalTimeOptions = ({
	selectedDeliveryOption,
	order,
	form,
	setEstimatedDeliveryData,
	selectedTimeOption,
	setSelectedTimeOption,
	orderDate,
	setEstimatingLoading,
	estimatingLoading,
}: Props) => {
	const { t } = useTranslation();
	const isWoltDriveSelected = selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE;
	const canChooseDeliveryTimeOption = isExternalOrderDelivery(order) || selectedDeliveryOption;
	const {
		setValue,
		control,
		formState: { errors },
	} = form;
	const { handleEstimateDelivery } = useEstimateDelivery({
		orderId: order.id,
		setEstimatedDeliveryData,
		setEstimatingLoading,
	});
	const organization = useSelector(selectOrganization);
	const defaultDeliveryOption = getDefaultDeliveryOption(organization, order);
	const showScheduledDeliveryAtDatePicker = isWoltDriveSelected;

	const getOptions = () => {
		if (isWoltDriveSelected) {
			return [...woltDriveTimeOptions].sort((a, b) => a - b).filter((option) => option !== 0);
		}

		return defaultDeliveryTimeOptions;
	};

	const handleSelect = (option: string | number | undefined) => {
		setValue("scheduled_delivery_at", "");
		if (option) handleEstimateDelivery(option);
	};

	const getHeaderTitle = () => {
		if (
			defaultDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE &&
			selectedDeliveryOption === LiveOrderDeliveryOptionType.WOLT_DRIVE
		) {
			return t("modules.live_order.field.choose_new_preparation_time_for_driver.title");
		}

		return !isWoltDriveSelected
			? t("modules.live_order.field.choose_delivery_time.title")
			: t("modules.live_order.field.choose_preparation_time_for_driver.title");
	};

	return canChooseDeliveryTimeOption ? (
		<div className="position-relative live-order-delivery-time-options">
			{estimatingLoading && <div className="overlay" />}
			<h5>{getHeaderTitle()}</h5>
			<div
				className={`d-flex flex-wrap gap-2 ${
					showScheduledDeliveryAtDatePicker ? "align-items-start" : "align-items-center"
				} live-order-delivery-time-options-container ${
					showScheduledDeliveryAtDatePicker ? "without-limited-height" : ""
				}`}
			>
				{getOptions().map((option) => (
					<LiveOrderDeliveryTimeOption
						setSelectedTimeOption={setSelectedTimeOption}
						option={option}
						selectedTimeOption={selectedTimeOption}
						key={option.toString()}
						handleSelect={handleSelect}
					/>
				))}
				{showScheduledDeliveryAtDatePicker && (
					<FormDatePicker
						control={control}
						name="scheduled_delivery_at"
						errors={errors}
						allowResetDate
						onApply={(date) => {
							setSelectedTimeOption(date ? formatDateTimeToString(date as Date) : undefined);
							if (date) {
								handleEstimateDelivery(formatDateTimeToString(date as Date));
							}
						}}
						help={t("modules.live_order.field.execution_at.help_text")}
						placeholder={t("modules.live_order.field.execution_at.placeholder")}
						onClear={() => {
							setSelectedTimeOption(undefined);
							setValue("scheduled_delivery_at", "");
						}}
						onToggle={(isOpen) => {
							if (isOpen && orderDate && getNumberOfMinutesFromNowToTheDate(orderDate) !== 0) {
								setSelectedTimeOption(orderDate);
								setValue("scheduled_delivery_at", orderDate);
							}
						}}
					/>
				)}
			</div>
		</div>
	) : (
		<></>
	);
};

export default LiveOrderAssignDeliveryEmployeeModalTimeOptions;
